import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Check, ArrowRight, Globe, Zap, Shield, Users, Layers, GitBranch, Cloud, RefreshCw, Database } from 'lucide-react';

// Common Dark Background Gradient for dark sections
const darkGradientStyle = {
    background: `
      radial-gradient(ellipse 60% 90% at 100% 50%, #3b82f6 0%, #1d4ed8 22%, #1e3a8a 45%, #0f172a 70%, #070b18 100%),
      linear-gradient(to right, #060b18 0%, #0f172a 30%, #1e3a8a 60%, #1d4ed8 100%)
    `,
    backgroundBlendMode: 'screen' as const,
};

// --- 1. HERO SECTION (DARK THEME) ---
const HeroSection = () => {
    const dashboards = [
        { 
            src: "/Logos/AWS Dashboard.png", 
            alt: "AWS Dashboard", 
            className: "top-0 left-0 lg:w-4/5 z-10",
            yAnimate: [0, -15, 0],
            duration: 5
        },
        { 
            src: "/Logos/Google Cloud Dashboard.png", 
            alt: "GCP Dashboard", 
            className: "top-1/3 right-0 lg:w-3/4 z-20",
            yAnimate: [0, 10, 0],
            duration: 6
        },
        { 
            src: "/Logos/AZURE Dashboard.png", 
            alt: "Azure Dashboard", 
            className: "bottom-0 -left-10 lg:w-4/5 z-0",
            yAnimate: [0, -10, 0],
            duration: 7
        },
        { 
            src: "/Logos/OCI Dashboard.png", 
            alt: "OCI Dashboard", 
            className: "bottom-1/4 -right-1/4 lg:w-1/2 z-10 opacity-70",
            yAnimate: [0, 5, 0],
            duration: 4
        },
    ];

    return (
        <section className="relative min-h-[90vh] overflow-hidden flex items-center pt-24 pb-16" style={darkGradientStyle}>
             <div className="container mx-auto px-4 z-10">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className="text-center lg:text-left">
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-5 text-white"
                        >
                            Architecting Your Cloud Future.
                            <span className="block text-blue-400">
                                Securely Delivered.
                            </span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                            className="text-slate-300 text-lg max-w-xl mx-auto lg:mx-0 mb-10"
                        >
                            We integrate, optimize, and secure cloud ecosystems for enterprises, ISPs, and governments — with precision, scalability, and unwavering trust.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                            className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
                        >
                            <Link
                                to="/contact"
                                className="group relative inline-flex items-center justify-center text-base font-bold px-8 py-4 rounded-md transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-500/25 bg-blue-600 text-white hover:bg-blue-500"
                            >
                                Start Your Cloud Journey
                            </Link>
                            <Link
                                to="/contact"
                                className="relative inline-flex items-center justify-center text-base font-bold px-8 py-4 rounded-md border-2 border-white/20 text-white transition-all duration-300 hover:bg-white/10"
                            >
                                Speak to a Cloud Architect
                            </Link>
                        </motion.div>
                    </div>

                    <div className="relative h-[550px] w-full hidden lg:block">
                         {dashboards.map((dash, index) => (
                            <motion.div
                                key={dash.alt}
                                className={`absolute ${dash.className}`}
                                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                                animate={{ 
                                    opacity: dash.alt === "OCI Dashboard" ? 0.7 : 1, 
                                    y: 0,
                                    scale: 1,
                                 }}
                                transition={{ duration: 0.8, delay: 0.4 + index * 0.2, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <motion.img
                                    src={dash.src}
                                    alt={dash.alt}
                                    className="rounded-lg shadow-2xl w-full border border-white/10"
                                    style={{ 
                                        boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5)`,
                                    }}
                                    animate={{ y: dash.yAnimate }}
                                    transition={{
                                        duration: dash.duration,
                                        repeat: Infinity,
                                        repeatType: 'mirror',
                                        ease: 'easeInOut'
                                    }}
                                />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- 2. CAPABILITIES SECTION (WHITE THEME - EYE CATCHY) ---
const CapabilitiesSection = () => {
    const capabilities = [
        { icon: <Cloud className="w-8 h-8" />, title: "Cloud Migration Excellence", description: "Seamless migration to AWS, Azure, GCP, OCI, or hybrid infrastructures." },
        { icon: <Layers className="w-8 h-8" />, title: "Multi-Cloud Strategy", description: "Resilient, highly scalable, and structurally cost-optimized cloud deployments." },
        { icon: <RefreshCw className="w-8 h-8" />, title: "Hybrid Cloud Enablement", description: "Secure and exceptionally agile public-private cloud environments integration." },
        { icon: <Shield className="w-8 h-8" />, title: "Cloud-Native Security", description: "Zero-Trust architecture, SOC-ready monitoring, and compliance-first data protection." },
        { icon: <GitBranch className="w-8 h-8" />, title: "Automation & DevOps", description: "Accelerated CI/CD pipelines, robust Infrastructure as Code, and Kubernetes optimization." },
        { icon: <Database className="w-8 h-8" />, title: "Business Continuity", description: "Enterprise-grade disaster recovery, intelligence backups, and failovers." }
    ];

    return (
        <section className="py-24 relative overflow-hidden bg-white">
            {/* Elegant, subtle high-tech patterned background for the white section */}
            <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:24px_24px] opacity-60 pointer-events-none" />
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white to-transparent pointer-events-none" />
            
            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                        Our Cloud Integration Capabilities
                    </h2>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        We leverage industry-leading innovations to design, transition, and manage robust cloud architectures customized for your organization's precise strategic needs.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {capabilities.map((cap, index) => (
                        <motion.div
                            key={index}
                            className="group relative bg-[#f8fafc] border border-slate-100 p-10 rounded-3xl transition-all duration-300 hover:bg-white hover:shadow-[0_20px_50px_rgba(37,99,235,0.08)] hover:-translate-y-2 overflow-hidden"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                        >
                            {/* Hover gradient border effect */}
                            <div className="absolute bottom-0 left-0 h-1 w-0 bg-blue-600 transition-all duration-300 group-hover:w-full" />
                            
                            <div className="relative inline-flex items-center justify-center h-16 w-16 mb-6 rounded-2xl bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-slate-100 text-blue-600 transition-transform duration-300 group-hover:scale-110">
                                {cap.icon}
                            </div>
                            <h3 className="text-2xl font-bold mb-4 text-slate-900">{cap.title}</h3>
                            <p className="text-slate-600 leading-relaxed font-light">{cap.description}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- 3. DIFFERENCE SECTION (DARK THEME) ---
const DifferenceSection = () => {
    const differences = [
        { icon: <Globe className="w-8 h-8 text-white" />, title: "Global Scale, Local Expertise", description: "Delivering for multinational giants with local insights." },
        { icon: <Zap className="w-8 h-8 text-white" />, title: "Engineered for Scale", description: "Architectures built to support millions of users without disruption." },
        { icon: <Shield className="w-8 h-8 text-white" />, title: "Security as Standard", description: "Solutions hardened against modern threats; compliance integrated." },
        { icon: <Users className="w-8 h-8 text-white" />, title: "Partnership, Not Provision", description: "Tailored solutions, full transparency, executive-level reporting." },
        { icon: <Check className="w-8 h-8 text-white" />, title: "Proven in Action", description: "Validated by industry certifications and leading client trust." }
    ];

    return (
        <section className="py-24 relative z-10 w-full overflow-hidden" style={darkGradientStyle}>
            <div className="container mx-auto px-4 relative z-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-center mb-16 text-white bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400">The Atlas Defenders Difference</h2>
                <div className="max-w-4xl mx-auto">
                    {differences.map((diff, index) => (
                        <motion.div
                            key={index}
                            className="flex items-start gap-6 mb-8 group"
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
                            viewport={{ once: true }}
                        >
                            <div className="flex-shrink-0 p-4 rounded-full bg-blue-600/20 border border-blue-500/30 group-hover:bg-blue-600/40 transition-colors duration-300">
                                {diff.icon}
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white mb-1">{diff.title}</h3>
                                <p className="text-blue-100/70">{diff.description}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- 4. TECHNOLOGIES & PARTNERSHIPS SECTION (WHITE THEME) ---
const TechPartnershipsSection = () => {
    const logos = [
        { name: "AWS", url: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Amazon_Web_Services_Logo.svg/1200px-Amazon_Web_Services_Logo.svg.png" },
        { name: "Azure", url: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg" },
        { name: "Google Cloud", url: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/51/Google_Cloud_logo.svg/1200px-Google_Cloud_logo.svg.png" },
        { name: "Oracle Cloud", url: "/Logos/oci_logo.png" },
        { name: "Hostinger", url: "/Logos/hotinger_logo.png" },
    ];

    return (
        <section className="py-24 bg-slate-50 relative overflow-hidden">
            {/* Subtle light glow behind logos */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-50/50 blur-3xl pointer-events-none rounded-full" />
            
            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center max-w-4xl mx-auto mb-20">
                    <h2 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 text-slate-900">
                        Integrated with the Leading Cloud Ecosystem
                    </h2>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Our cloud integration solutions are strategically built on the most powerful, highly available, and undeniably reliable cloud platforms globally—ensuring flawless scalability and peak performance under any workload.
                    </p>
                </div>
                <div className="flex justify-center flex-wrap gap-12 lg:gap-20 items-center max-w-[1000px] mx-auto">
                    {logos.map((logo) => (
                        <motion.div
                            key={logo.name}
                            whileHover={{ scale: 1.05 }}
                            className="transition-all duration-300"
                        >
                            <div className="bg-white border border-slate-100 p-8 rounded-3xl shadow-[0_4px_30px_rgba(0,0,0,0.03)] flex items-center justify-center min-w-[220px] h-[130px] hover:shadow-[0_8px_40px_rgba(37,99,235,0.06)] transition-all duration-300">
                              <img 
                                  src={logo.url} 
                                  alt={`${logo.name} logo`} 
                                  className={`object-contain ${logo.name === 'Hostinger' ? 'h-12' : 'h-10'}`}
                              />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- 5. CERTIFICATIONS SECTION (DARK THEME) ---
const CertificationsSection = () => {
    const certifications = [
        { name: "AWS Certified Solution Architect Professional", url: "/Certifications/AWS Certified Solution Architect Professional.png" },
        { name: "Microsoft Certified: Azure Administrator Associate", url: "/Certifications/Az 104.png" },
        { name: "Google Cloud Associate Cloud Engineer", url: "/Certifications/Associate Cloud Engineer Certification.png" },
        { name: "Google Cloud Professional Cloud Architect", url: "/Certifications/Professional Cloud Architect Certification.png" },
        { name: "Certified Kubernetes Administrator", url: "/Certifications/Certified Kubernetes Administrator.png" },
    ];

    return (
        <section className="py-24 border-b border-t border-blue-900/40" style={darkGradientStyle}>
            <div className="container mx-auto px-4">
                <div className="text-center mb-20">
                    <h2 className="text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-4 text-white">
                        Certified Excellence
                    </h2>
                    <p className="text-lg lg:text-xl max-w-3xl mx-auto text-blue-100/80">
                        Validated by the industry's most respected credentials, our team's deep technical expertise stands as the resilient bedrock of your infrastructure's success.
                    </p>
                </div>
                <div className="flex justify-center flex-wrap gap-12 items-center">
                    {certifications.map((cert) => (
                        <motion.div
                            key={cert.name}
                            className="text-center transition-all duration-300 transform hover:scale-105"
                        >
                            <div className="relative h-32 w-32 mx-auto mb-6 p-2 bg-white/5 rounded-full border border-white/10 backdrop-blur-sm shadow-2xl">
                                <img 
                                    src={cert.url} 
                                    alt={`${cert.name} badge`} 
                                    className="h-full w-full object-contain rounded-full"
                                />
                            </div>
                            <p className="font-medium text-sm max-w-[200px] mx-auto text-slate-300 leading-snug">{cert.name}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- 6. TESTIMONIALS SECTION (WHITE THEME) ---
const TestimonialsSection = () => {
    const testimonials = [
        {
            quote: "Seamless hybrid cloud integration across AWS, Azure, and on-prem systems — delivering 40% faster transaction processing and full regulatory compliance.",
            title: "CTO, Global Bank"
        },
        {
            quote: "HIPAA-compliant, secure cloud infrastructure for patient data — fully encrypted at rest and in transit.",
            title: "Head of IT, Public Hospital"
        },
        {
            quote: "Scalable cloud platforms to support 10,000+ simultaneous students and researchers accessing virtual labs.",
            title: "CIO, National Research University"
        },
        {
            quote: "Carrier-grade cloud solutions supporting millions of concurrent users with 99.999% uptime.",
            title: "CTO, Major African ISP"
        },
        {
            quote: "Flexible cloud infrastructure that scales automatically with business growth.",
            title: "IT Director, Fortune 500 Enterprise"
        },
        {
            quote: "Real-time risk monitoring and zero-trust security architecture for sensitive financial data.",
            title: "CIO, International Investment Firm"
        },
    ];

    const duplicatedTestimonials = [...testimonials, ...testimonials];

    return (
        <section className="py-32 overflow-hidden bg-white relative">
            <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
            <div className="container mx-auto px-4 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                        Voices of Transformation
                    </h2>
                    <p className="text-lg text-slate-600 leading-relaxed">
                        Discover how we've empowered ambitious global organizations to fully actualize their cloud strategic goals and achieve unhindered digital resilience.
                    </p>
                </div>
                
                <div className="relative">
                     <motion.div 
                        className="flex gap-8"
                        animate={{
                            x: ['-0%', '-100%'],
                            transition: {
                                ease: 'linear',
                                duration: 40,
                                repeat: Infinity,
                            }
                        }}
                     >
                        {duplicatedTestimonials.map((testimonial, index) => (
                            <div key={index} className="flex-shrink-0 w-[420px] p-10 rounded-3xl bg-white border border-slate-100 shadow-[0_10px_40px_rgba(0,0,0,0.04)] flex flex-col justify-between group hover:shadow-[0_20px_50px_rgba(37,99,235,0.06)] transition-all duration-300">
                                <p className="text-lg leading-relaxed text-slate-600 font-light mb-8">"{testimonial.quote}"</p>
                                <div className="border-t border-slate-100 pt-6 mt-auto">
                                    <p className="font-bold text-blue-800 text-lg group-hover:text-blue-600 transition-colors">{testimonial.title}</p>
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

// --- MAIN PAGE COMPONENT ---
const CloudIntegrationPage = () => {
    useEffect(() => {
        document.title = 'Cloud Integration | Atlas Defenders';
    }, []);

    return (
        <div className="relative w-full overflow-hidden bg-white">
            <HeroSection />
            <CapabilitiesSection />
            <DifferenceSection />
            <TechPartnershipsSection />
            <CertificationsSection />
            <TestimonialsSection />
        </div>
    );
};

export default CloudIntegrationPage;
